import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { formatAdsTime } from 'utils/date';
import { calcSizeForTv } from 'utils/index';

import { Icons } from 'components/Icons';
import { AdsState } from 'components/Player/SpallaPlayerCTV/index';
import { usePlayer } from 'components/Player/context';
import { WebDiv } from 'components/WebDiv';

type CTVAdsControlsProps = {
  adsState: AdsState;
};

const CTVAdsControls = ({ adsState }: CTVAdsControlsProps): React.ReactPortal | null => {
  const { ref: spallaRef, isVisible, play, pause, isPlaying } = usePlayer();
  const [container, setContainer] = useState<Element | null>(null);
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);

  const spallaPlayer = spallaRef?.current as HTMLDivElement;

  const adsRemainingTime = Math.max(adsState.adsDuration - adsState.adsCurrentTime, 0);

  const adsRemainingTimeFormatted = formatAdsTime(adsRemainingTime);

  const TogglePlayPauseIcon = isPlaying ? Icons.SolidPause : Icons.Play;

  useEffect(() => {
    if (spallaPlayer instanceof HTMLElement) {
      setIframe(spallaPlayer.querySelector('iframe'));
    }
  }, [spallaRef]);

  const sendAction = (action: 'play' | 'pause') => {
    iframe?.contentWindow?.postMessage({ action }, '*');
    if (action === 'play') play();
    if (action === 'pause') pause();
  };

  const togglePlayPause = () => {
    sendAction(isPlaying ? 'pause' : 'play');
  };

  useEffect(() => {
    sendAction(isVisible ? 'play' : 'pause');
  }, [isVisible]);

  useEffect(() => {
    if (spallaPlayer?.parentNode) {
      let controls = spallaPlayer?.parentNode?.querySelector?.('#spalla-ctv-ads-controls');
      if (!controls) {
        controls = document.createElement('div');
        controls.id = 'spalla-ctv-ads-controls';
        spallaPlayer?.parentNode?.appendChild(controls);
      }
      setContainer(controls);
    }
  }, [spallaPlayer]);

  if (!container || !adsState) return null;

  return createPortal(
    <WebDiv className="navigation-container navigation-priority">
      <View style={styles.adsContainer}>
        <View style={styles.adsTopControlsContainer}>
          <Text style={styles.adsTagText}>Anúncio</Text>
          <Text style={styles.adsTime}>{adsRemainingTimeFormatted}</Text>
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center', opacity: 0 }}>
          <Pressable
            accessibilityRole="button"
            onPress={togglePlayPause}
            style={styles.playPauseButton}>
            <TogglePlayPauseIcon
              width={calcSizeForTv(40).toString()}
              height={calcSizeForTv(40).toString()}
            />
          </Pressable>
        </View>
      </View>
    </WebDiv>,
    container
  );
};

const styles = StyleSheet.create({
  adsContainer: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 100,
  },
  adsTopControlsContainer: {
    flexDirection: 'row-reverse',
    position: 'relative',
    alignItems: 'center',
    margin: calcSizeForTv(32),
  },
  backButton: {
    marginRight: calcSizeForTv(40),
    padding: calcSizeForTv(4),
  },
  adsTagText: {
    backgroundColor: 'yellow',
    paddingHorizontal: calcSizeForTv(16),
    paddingVertical: calcSizeForTv(12),
    borderRadius: 8,
    color: 'black',
    fontSize: calcSizeForTv(24),
    fontWeight: '600',
  },
  adsTime: {
    backgroundColor: 'rgba(28, 28, 28, 0.66)',
    textAlign: 'center',
    paddingVertical: calcSizeForTv(12),
    borderRadius: 8,
    marginRight: calcSizeForTv(8),
    color: '#FFF',
    fontSize: calcSizeForTv(24),
    fontWeight: '600',
    width: calcSizeForTv(100),
  },
  playPauseButton: {
    padding: calcSizeForTv(8),
  },
});

export default CTVAdsControls;
